/**
 * Basecamp Projects Focus - prototype panel (read-only live/cached data)
 *
 * Used by ?test=basecamp-extension scene `projects-focus`.
 * Mirrors Documents browse-folder-focus: auto-hide inactive, cap, Show N hidden,
 * Always show / Hide overrides. Activity ranked from Maater signals (bookmarks +
 * local touch), not Basecamp web-app opens.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Eye, FolderOpen, MoreHorizontal, Radio, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useBasecampAuth, useBasecampProjects } from '@/hooks/use-basecamp'
import { basecampApi } from '@/lib/basecamp-api'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import type { BasecampProject } from '@/types/basecamp'

const FOCUS_MAX_VISIBLE = 8
const PREFS_KEY = 'flownotes-bc-project-focus-v1'
const LISTENING_KEY = 'flownotes-bc-project-listening-mock-v1'

type ProjectMode = 'default' | 'always' | 'hidden'

type FocusPrefs = {
  alwaysShowIds: string[]
  hiddenManualIds: string[]
  /** Last time user opened/touched this project in Maater (prototype local). */
  touchedAt: Record<string, string>
}

type RankedProject = {
  project: BasecampProject
  id: string
  score: number
  bookmarkCount: number
  mode: ProjectMode
  listening: boolean
}

function loadPrefs(): FocusPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    if (!raw) return { alwaysShowIds: [], hiddenManualIds: [], touchedAt: {} }
    const parsed = JSON.parse(raw) as Partial<FocusPrefs>
    return {
      alwaysShowIds: parsed.alwaysShowIds ?? [],
      hiddenManualIds: parsed.hiddenManualIds ?? [],
      touchedAt: parsed.touchedAt ?? {},
    }
  } catch {
    return { alwaysShowIds: [], hiddenManualIds: [], touchedAt: {} }
  }
}

function savePrefs(prefs: FocusPrefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs))
}

function loadListeningIds(): Set<string> {
  try {
    const raw = localStorage.getItem(LISTENING_KEY)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as string[])
  } catch {
    return new Set()
  }
}

function saveListeningIds(ids: Set<string>) {
  localStorage.setItem(LISTENING_KEY, JSON.stringify([...ids]))
}

function modeFor(id: string, prefs: FocusPrefs): ProjectMode {
  if (prefs.hiddenManualIds.includes(id)) return 'hidden'
  if (prefs.alwaysShowIds.includes(id)) return 'always'
  return 'default'
}

function setMode(prefs: FocusPrefs, id: string, mode: ProjectMode): FocusPrefs {
  const alwaysShowIds = prefs.alwaysShowIds.filter((x) => x !== id)
  const hiddenManualIds = prefs.hiddenManualIds.filter((x) => x !== id)
  if (mode === 'always') alwaysShowIds.push(id)
  if (mode === 'hidden') hiddenManualIds.push(id)
  return { ...prefs, alwaysShowIds, hiddenManualIds }
}

export function ProjectsFocusPanel({
  onOpenProject,
  onOpenListening,
}: {
  onOpenProject?: (projectId: string, projectName: string) => void
  onOpenListening?: () => void
}) {
  const { authStatus, isLoading: authLoading } = useBasecampAuth()
  const { projects, isLoading, error, refetch } = useBasecampProjects()
  const [prefs, setPrefs] = useState<FocusPrefs>(() => loadPrefs())
  const [listeningIds, setListeningIds] = useState<Set<string>>(() => loadListeningIds())
  const [bookmarkCounts, setBookmarkCounts] = useState<Record<string, number>>({})
  const [revealHidden, setRevealHidden] = useState(false)
  const [seededListening, setSeededListening] = useState(false)
  const [query, setQuery] = useState('')
  const [listeningFilter, setListeningFilter] = useState<'all' | 'on' | 'off'>('all')

  useEffect(() => {
    savePrefs(prefs)
  }, [prefs])

  useEffect(() => {
    saveListeningIds(listeningIds)
  }, [listeningIds])

  // Bookmarks -> per-project activity weight (cached DB via API)
  useEffect(() => {
    if (!authStatus?.authenticated) return
    let cancelled = false
    ;(async () => {
      try {
        const data = await basecampApi.getMyBookmarks(false)
        if (cancelled) return
        const counts: Record<string, number> = {}
        for (const item of data.items || []) {
          const pid = String(
            item.project?.id ?? item.project_id ?? item.bucket_id ?? '',
          )
          if (!pid) continue
          counts[pid] = (counts[pid] ?? 0) + 1
        }
        setBookmarkCounts(counts)
      } catch {
        /* read-only prototype - ignore bookmark load errors */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authStatus?.authenticated])

  // Seed Listening mock for top bookmarked/touched projects once
  useEffect(() => {
    if (seededListening || listeningIds.size > 0 || projects.length === 0) return
    const top = [...projects]
      .map((p) => ({
        id: String(p.id),
        score: bookmarkCounts[String(p.id)] ?? 0,
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((x) => x.id)
    if (top.length === 0) return
    setListeningIds(new Set(top))
    setSeededListening(true)
  }, [projects, bookmarkCounts, listeningIds.size, seededListening])

  const ranked: RankedProject[] = useMemo(() => {
    return projects
      .filter((p): p is BasecampProject => p != null && p.id != null)
      .map((project) => {
        const id = String(project.id)
        const bookmarkCount = bookmarkCounts[id] ?? 0
        const touchedMs = prefs.touchedAt[id]
          ? new Date(prefs.touchedAt[id]).getTime()
          : 0
        const mode = modeFor(id, prefs)
        // Score: always-show ranks first; then recent touch; then bookmarks
        let score = 0
        if (mode === 'always') score = 1e15 + bookmarkCount
        else if (mode === 'hidden') score = -1
        else score = touchedMs + bookmarkCount * 86_400_000 // each bookmark ~1 day weight
        return {
          project,
          id,
          score,
          bookmarkCount,
          mode,
          listening: listeningIds.has(id),
        }
      })
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score
        return a.project.name.localeCompare(b.project.name)
      })
  }, [projects, bookmarkCounts, prefs, listeningIds])

  const filteredRanked = useMemo(() => {
    const q = query.trim().toLowerCase()
    return ranked.filter((r) => {
      if (listeningFilter === 'on' && !r.listening) return false
      if (listeningFilter === 'off' && r.listening) return false
      if (q && !r.project.name.toLowerCase().includes(q)) return false
      return true
    })
  }, [ranked, query, listeningFilter])

  const { visible, hiddenCount } = useMemo(() => {
    if (revealHidden) {
      return {
        visible: filteredRanked.filter((r) => r.mode !== 'hidden'),
        hiddenCount: filteredRanked.filter((r) => r.mode === 'hidden').length,
      }
    }
    const always = filteredRanked.filter((r) => r.mode === 'always')
    const candidates = filteredRanked.filter((r) => r.mode === 'default' && r.score > 0)
    const inactive = filteredRanked.filter((r) => r.mode === 'default' && r.score <= 0)
    const manualHidden = filteredRanked.filter((r) => r.mode === 'hidden')

    const slots = Math.max(0, FOCUS_MAX_VISIBLE - always.length)
    const capped = candidates.slice(0, slots)
    const visibleIds = new Set([...always, ...capped].map((r) => r.id))
    const autoHidden = [...candidates.slice(slots), ...inactive]

    return {
      visible: filteredRanked.filter((r) => visibleIds.has(r.id)),
      hiddenCount: autoHidden.length + manualHidden.length,
    }
  }, [filteredRanked, revealHidden])

  const touchProject = useCallback((id: string) => {
    setPrefs((prev) => ({
      ...prev,
      touchedAt: { ...prev.touchedAt, [id]: new Date().toISOString() },
    }))
  }, [])

  const openOAuth = () => {
    window.open(
      authStatus?.auth_url || '/auth/basecamp',
      'basecamp-auth',
      'width=600,height=700',
    )
  }

  if (authLoading || (authStatus?.authenticated && isLoading && projects.length === 0)) {
    return (
      <div
        className="flex h-full items-center justify-center text-sm text-muted-foreground"
        data-testid={testIds.basecampExtensionTest.projectsFocusPanel}
      >
        Loading projects from Basecamp cache…
      </div>
    )
  }

  if (!authStatus?.authenticated || error?.status === 401) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center"
        data-testid={testIds.basecampExtensionTest.projectsFocusPanel}
      >
        <FolderOpen className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm font-medium">Connect Basecamp to load real projects</p>
        <p className="max-w-sm text-xs text-muted-foreground">
          Read-only. Uses your OAuth session and cached `basecamp_projects` /
          bookmarks from the local DB.
        </p>
        <Button size="sm" onClick={openOAuth}>
          Connect Basecamp
        </Button>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center"
        data-testid={testIds.basecampExtensionTest.projectsFocusPanel}
      >
        <p className="text-sm text-muted-foreground">
          {error.message || 'Failed to load projects'}
        </p>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div
      className="flex h-full flex-col"
      data-testid={testIds.basecampExtensionTest.projectsFocusPanel}
    >
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-6 py-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">Projects</h1>
            <Badge variant="secondary" className="font-normal">
              {visible.length}
              {!revealHidden && filteredRanked.length > visible.length
                ? ` of ${filteredRanked.length}`
                : filteredRanked.length === visible.length
                  ? ''
                  : ` / ${filteredRanked.length}`}
            </Badge>
            <Badge variant="outline" className="font-normal text-[10px]">
              focus
            </Badge>
          </div>
          <p className="mt-1 max-w-lg text-xs text-muted-foreground">
            Auto-hide inactive projects (Documents focus pattern). Ranked by Maater
            touch + bookmarks. Listening badges are a local mock until Triggers ships.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Refresh
          </Button>
          {onOpenListening ? (
            <Button size="sm" variant="ghost" onClick={onOpenListening}>
              Listening
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-2.5">
        {(['all', 'on', 'off'] as const).map((id) => (
          <Button
            key={id}
            size="sm"
            variant={listeningFilter === id ? 'secondary' : 'ghost'}
            className="h-7 text-xs"
            onClick={() => setListeningFilter(id)}
          >
            {id === 'all' ? 'All' : id === 'on' ? 'Listening' : 'Off'}
          </Button>
        ))}
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter projects..."
            className="h-7 pl-7 text-xs"
            aria-label="Filter projects"
          />
        </div>
      </div>

      <ul className="flex-1 overflow-auto">
        {visible.map((row) => (
          <li key={row.id}>
            <div
              className={cn(
                'flex w-full items-center gap-3 border-b border-border/60 px-6 py-3',
                'hover:bg-accent/40',
              )}
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 items-start gap-3 text-left"
                onClick={() => {
                  touchProject(row.id)
                  onOpenProject?.(row.id, row.project.name)
                }}
              >
                <FolderOpen className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium">{row.project.name}</span>
                    {row.mode === 'always' ? (
                      <Badge variant="outline" className="font-normal text-[10px]">
                        Always show
                      </Badge>
                    ) : null}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {row.listening
                      ? 'Listening · mock'
                      : 'Not listening'}
                    {row.bookmarkCount > 0
                      ? ` · ${row.bookmarkCount} bookmark${row.bookmarkCount === 1 ? '' : 's'}`
                      : ''}
                    {prefs.touchedAt[row.id]
                      ? ` · touched ${new Date(prefs.touchedAt[row.id]).toLocaleDateString()}`
                      : ''}
                  </div>
                </div>
              </button>
              {row.listening ? (
                <Badge variant="secondary" className="gap-1 font-normal">
                  <Radio className="h-3 w-3" />
                  On
                </Badge>
              ) : (
                <Badge variant="outline" className="font-normal">
                  Off
                </Badge>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    data-testid={testIds.basecampExtensionTest.projectsFocusRowMenu(row.id)}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => setPrefs((p) => setMode(p, row.id, 'always'))}
                  >
                    Always show in list
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setPrefs((p) => setMode(p, row.id, 'hidden'))}
                  >
                    Hide from browse
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setPrefs((p) => setMode(p, row.id, 'default'))}
                  >
                    Auto (activity rules)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setListeningIds((prev) => {
                        const next = new Set(prev)
                        if (next.has(row.id)) next.delete(row.id)
                        else next.add(row.id)
                        return next
                      })
                    }}
                  >
                    {row.listening ? 'Mock: turn Listening off' : 'Mock: turn Listening on'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </li>
        ))}

        {!revealHidden && hiddenCount > 0 ? (
          <li>
            <button
              type="button"
              className="flex w-full items-center gap-3 px-6 py-3 text-left text-sm italic text-muted-foreground hover:bg-accent/30"
              data-testid={testIds.basecampExtensionTest.projectsFocusShowHidden}
              onClick={() => setRevealHidden(true)}
            >
              <Eye className="h-4 w-4 shrink-0" />
              Show {hiddenCount} hidden project{hiddenCount === 1 ? '' : 's'}
            </button>
          </li>
        ) : null}

        {revealHidden && hiddenCount > 0 ? (
          <li>
            <button
              type="button"
              className="flex w-full items-center gap-3 border-t border-border px-6 py-3 text-left text-xs text-muted-foreground hover:bg-accent/30"
              onClick={() => setRevealHidden(false)}
            >
              Collapse to focused list ({FOCUS_MAX_VISIBLE} max)
            </button>
          </li>
        ) : null}

        {revealHidden
          ? ranked
              .filter((r) => r.mode === 'hidden' || !visible.some((v) => v.id === r.id))
              .map((row) => (
                <li key={`hidden-${row.id}`}>
                  <div className="flex items-center gap-3 border-b border-border/40 px-6 py-2.5 opacity-70">
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate text-sm">{row.project.name}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {row.mode === 'hidden' ? 'manual hide' : 'auto-hide'}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => setPrefs((p) => setMode(p, row.id, 'always'))}
                    >
                      Always show
                    </Button>
                  </div>
                </li>
              ))
          : null}
      </ul>
    </div>
  )
}
